import React, { ReactNode } from 'react';
import { format } from 'date-fns';
import { PermissionCode, UserType } from './enums';
import StyledChip from '../components/styled-chip';
import {
    DaySnapshot,
    MealItemSnapshot,
    MealSnapshot,
    NutritionCount,
} from '../service/model/nutrition.model';
import { LoggedInUserResponseModel } from '../service/model/logged-in-user-response.model';
import { User } from '../service/model/user.model';

type MembershipStatus = 'Active' | 'Expired' | string;

export const isAdmin = (user?: User): boolean => {
    return user?.userType === UserType.GYMINIFY;
};

// export const isOwner = (user?: BusinessUser): boolean => {
//     if (!user) return false;
//     return user.role.name === UserRoles.Owner;
// };

// export const isStaff = (roles: Role[]): boolean => {
//     return roles.some((role) => role.name === UserRoles.Staff);
// };

// export const isMember = (roles: Role[]): boolean => {
//     return roles.some((role) => role.name === UserRoles.Member);
// };

export const isBusinessUser = (user?: User): boolean => {
    return user?.userType === UserType.BUSINESS_USER;
};

export const getCurrentDate = (): string => {
    return format(new Date(), 'yyyy-MM-dd');
};

export const getFullName = (firstName?: string, lastName?: string): string => {
    if (!firstName && !lastName) {
        return '';
    }
    if (!firstName) {
        return lastName as string;
    }
    if (!lastName) {
        return firstName as string;
    }
    return `${firstName} ${lastName}`;
};

export const getMembershipStatusChip = (
    status: MembershipStatus,
): ReactNode => {
    switch (status) {
        case 'Active': {
            return React.createElement(StyledChip, {
                label: status,
                color: 'success',
            });
        }
        case 'Expired': {
            return React.createElement(StyledChip, {
                label: status,
                color: 'error',
            });
        }
        default: {
            return React.createElement(React.Fragment, null);
        }
    }
};

export const toTitleCase = (text: string): string => {
    return text
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const getNutritionCount = (
    defaultQuantity?: number,
    actualQuantity?: number,
    nutrientValue?: number,
): number => {
    if (!nutrientValue) return 0;
    if (!defaultQuantity || !actualQuantity) return 0;
    const calculatedValue = (actualQuantity / defaultQuantity) * nutrientValue;
    return Number(calculatedValue.toFixed(2));
};

export const getTotalMealNutrition = (
    mealItems: MealItemSnapshot[],
): NutritionCount => {
    return mealItems.reduce(
        (total, item) => {
            return {
                calories: Number(
                    (total.calories + (item.nutrition?.calories ?? 0)).toFixed(
                        2,
                    ),
                ),
                protein: Number(
                    (total.protein + (item.nutrition?.protein ?? 0)).toFixed(2),
                ),
                carbohydrates: Number(
                    (
                        total.carbohydrates +
                        (item.nutrition?.carbohydrates ?? 0)
                    ).toFixed(2),
                ),
                fat: Number(
                    (total.fat + (item.nutrition?.fat ?? 0)).toFixed(2),
                ),
                fiber: Number(
                    (total.fiber + (item.nutrition?.fiber ?? 0)).toFixed(2),
                ),
                sugar: Number(
                    (total.sugar + (item.nutrition?.sugar ?? 0)).toFixed(2),
                ),
            };
        },
        {
            calories: 0,
            protein: 0,
            carbohydrates: 0,
            fat: 0,
            fiber: 0,
            sugar: 0,
        },
    );
};

export const getTotalDayNutrition = (meals: MealSnapshot[]): NutritionCount => {
    return meals.reduce(
        (total, meal) => {
            const totalMealNutrition = getTotalMealNutrition(meal.items);
            return {
                calories: Number(
                    (total.calories + totalMealNutrition.calories).toFixed(2),
                ),
                protein: Number(
                    (total.protein + totalMealNutrition.protein).toFixed(2),
                ),
                carbohydrates: Number(
                    (
                        total.carbohydrates + totalMealNutrition.carbohydrates
                    ).toFixed(2),
                ),
                fat: Number((total.fat + totalMealNutrition.fat).toFixed(2)),
                fiber: Number(
                    (total.fiber + totalMealNutrition.fiber).toFixed(2),
                ),
                sugar: Number(
                    (total.sugar + totalMealNutrition.sugar).toFixed(2),
                ),
            };
        },
        {
            calories: 0,
            protein: 0,
            carbohydrates: 0,
            fat: 0,
            fiber: 0,
            sugar: 0,
        },
    );
};

export const getTotalPlanNutrition = (days: DaySnapshot[]): NutritionCount => {
    return days.reduce(
        (total, day) => {
            return {
                calories:
                    total.calories + getTotalDayNutrition(day.meals).calories,
                protein:
                    total.protein + getTotalDayNutrition(day.meals).protein,
                carbohydrates:
                    total.carbohydrates +
                    getTotalDayNutrition(day.meals).carbohydrates,
                fat: total.fat + getTotalDayNutrition(day.meals).fat,
                fiber: total.fiber + getTotalDayNutrition(day.meals).fiber,
                sugar: total.sugar + getTotalDayNutrition(day.meals).sugar,
            };
        },
        {
            calories: 0,
            protein: 0,
            carbohydrates: 0,
            fat: 0,
            fiber: 0,
            sugar: 0,
        },
    );
};

export const getPermissionsForLoggedInUser = (
    loggedInUser?: LoggedInUserResponseModel,
): PermissionCode[] => {
    if (!loggedInUser) {
        return [];
    }
    return loggedInUser?.roleData?.businessUser?.role?.permissions?.map(permission => permission.code as PermissionCode) ?? [];
};
